import React, { FC, useCallback, useEffect, useState } from 'react';
import { Box, Flex, Heading, Text } from '@resideo/blueprint-react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import SeparatorLine from 'components/common/SeparatorLine';
import { convertMoneyAmount } from 'routes/ServicesAndSubscriptions/Cart';
import RecaliberationInstructionsModal from 'components/cart/order/RecaliberationInstructionsModal';
import { ReactComponent as PaypalIcon } from 'assets/images/cart/icon_Paypal.svg';
import CustomPopup from 'components/common/customPopup/customPopup.style';
import CartItem from 'components/cart/CartItem';
import { Link } from 'react-router-dom';
import { graphql } from 'react-relay/hooks';
import { useParams } from 'react-router';
import { fetchQuery } from 'relay-runtime';
import { useRelayEnvironment } from 'relay-hooks';
import moment from 'moment';
import PageLoading from 'components/common/PageLoading';
import { OrderCompleteQuery } from './__generated__/OrderCompleteQuery.graphql';
import { subscriptionOrderProducts } from 'data/SelectPlanData';
import {
  CreditCardTypeImage,
  getSmallerCCimg,
} from 'components/cart/reviewOrder/PaymentMethod';
import { maskPhoneNumber } from 'utils/maskPhoneNumber';
import { getFullCountryByCode } from 'config/countries';
import './Order.css';

const PageTitle = styled.div`
  text-transform: uppercase;
  padding-bottom: 1.5rem;
  color: ${({ theme }) => theme.colors.textLight3};
  font-family: 'URW_Din_CondBold';
  font-size: 2.75rem;
  line-height: 5rem;
  letter-spacing: 1.47px;
`;

const Print = styled(Link)`
  cursor: pointer;
  font-weight: 500;
  text-decoration: none;
  color: ${({ theme }) => theme.colors.cartLink};
`;

const FlexBox = styled(Flex)`
  @media (max-width: 768px) {
    border-radius: 15px;
  }
`;

const HeadingBox = styled(Heading)`
  margin-bottom: 1.063rem;
  font-family: 'Roboto';
  font-weight: bold;
`;
const TextBox = styled(Text)`
  line-height: 24px;
`;

const LabelStrong = styled.span`
  font-family: 'Roboto';
  font-weight: 700;
`;

const LabelValue = styled.span`
  margin-left: 0.75rem;
`;

const ThankYou: FC<any> = ({ viewerData }) => {
  const params = useParams() as any;
  const orderId = params?.id;
  const [isLoading, setLoading] = useState(true);
  const [order, setOrder] = useState<any>(null);
  const environment = useRelayEnvironment();
  const { t } = useTranslation();

  const [hasSubmittedDevice, setHasSubmittedDevice] = useState(false);

  const [openInstructionsModal, setOpenInstructionsModal] = useState(false);
  const closeAddAddressModal = () => setOpenInstructionsModal(false);

  const today = moment(order?.createdAt).format('MMM DD, YYYY');

  const invoiceData = useCallback(() => {
    if (orderId !== '' && orderId !== undefined) {
      fetchQuery<OrderCompleteQuery>(
        environment,
        graphql`
          query OrderCompleteQuery($orderId: ID!) {
            order: node(id: $orderId) {
              ... on DigitalRiverOrder {
                billTo {
                  address {
                    addressLine1
                    addressLine2
                    city
                    contactName
                    countryCode
                    mailingAddressName
                    stateProvinceRegionCode
                    zipPostalCode
                  }
                  email
                  firstName
                  lastName
                  organization
                }
                createdAt
                currency
                id
                invoiceNumber
                lineItems {
                  sku
                  quantity
                  amount {
                    amount
                    currencyCode
                  }
                  billingPeriodStartDate
                  billingPeriodEndDate
                  deviceNumber
                }
                shipTo {
                  address {
                    addressLine1
                    addressLine2
                    city
                    contactName
                    countryCode
                    mailingAddressName
                    stateProvinceRegionCode
                    zipPostalCode
                  }
                  email
                  firstName
                  lastName
                  organization
                }
                paymentSource {
                  paymentSessionId
                  clientSecret
                  state
                  currency
                  type
                  currency
                  creditCard {
                    brand
                    expirationMonth
                    expirationYear
                    lastFourDigits
                  }
                }

                subTotal {
                  amount
                  currencyCode
                }
                tax {
                  amount
                  currencyCode
                }
                taxRate
                total {
                  amount
                  currencyCode
                }
                totalShipping {
                  amount
                  currencyCode
                }
              }
            }
          }
        `,
        {
          orderId,
        }
      ).subscribe({
        next: data => {
          const theOrder = {
            ...data?.order,
            lineItems: data?.order?.lineItems?.map(ele => {
              // convert amount to 'cents', then use Math.round and toFixed to avoid rounding errors
              const centsAmount = ele?.amount?.amount
                ? Math.round(parseFloat(`${ele.amount.amount}`) * 100)
                : 0;
              const qty = ele?.quantity ?? 1;
              const price = (Math.round(centsAmount / qty) / 100).toFixed(2);
              return {
                ...ele,
                ...(subscriptionOrderProducts.find(
                  item => item.sku === ele?.sku
                ) ?? {
                  name: 'Product Not Found',
                  type: 'Unknown',
                }),
                price,
              };
            }),
          };
          setOrder(theOrder);
          setLoading(false);
        },
        error: () => {
          setOrder({});
          setLoading(false);
        },
      });
    } else {
      setLoading(false);
      setOrder({});
    }
  }, [environment, orderId]);

  useEffect(() => {
    invoiceData();
  }, [orderId]);

  useEffect(() => {
    history.pushState(null, '', location.href);
    window.onpopstate = function() {
      history.go(1);
    };
  }, []);

  const hasProduct =
    order?.lineItems?.filter(f => f.type === 'product').length > 0;

  const taxData = JSON.parse(
    `${localStorage.getItem('taxExemptionApplication')}`
  );

  return isLoading || order === null ? (
    <PageLoading />
  ) : (
    <>
      {openInstructionsModal && (
        <CustomPopup open={openInstructionsModal} closeOnDocumentClick={false}>
          <RecaliberationInstructionsModal
            close={closeAddAddressModal}
            hasSubmittedDevice={hasSubmittedDevice}
            setHasSubmittedDevice={setHasSubmittedDevice}
          />
        </CustomPopup>
      )}
      <Box padding='40px'>
        <PageTitle>{t('cart.orderComplete.thankyou')}</PageTitle>
        <Text color='textHeading' fontFamily='Roboto'>
          {t('cart.orderComplete.orderPlaced')} {viewerData?.contactEmail}
          <Text color='textHeading' fontFamily='Roboto'></Text>
        </Text>
        <SeparatorLine />
        <Flex paddingBottom='small' display={['', '', 'none', 'none']}>
          <Print
            to={`/cart/print-friendly-view/${orderId}`}
            color='primary'
            target='_blank'>
            {t('cart.orderComplete.printFriendlyView')}
          </Print>
        </Flex>
        <Flex justifyContent='space-between' alignItems='flex-end'>
          <Flex flexDirection='column'>
            <Text color='header.secondaryLinkActive'>
              <LabelStrong>{t('cart.order.orderNumber')}</LabelStrong>
              <LabelValue>{order?.invoiceNumber}</LabelValue>
            </Text>
            <Text paddingY='small' color='header.secondaryLinkActive'>
              <LabelStrong>{t('cart.order.date')}</LabelStrong>
              <LabelValue>{today}</LabelValue>
            </Text>
            <Text color='header.secondaryLinkActive'>
              <LabelStrong>{t('cart.order.orderStatus')}</LabelStrong>
              <LabelValue>{t('cart.order.completed')}</LabelValue>
            </Text>
          </Flex>
          <Flex display={['none', 'none', 'flex', 'flex']}>
            <Print
              to={`/cart/print-friendly-view/${orderId}`}
              color='primary'
              target='_blank'>
              {t('cart.orderComplete.printFriendlyView')}
            </Print>
          </Flex>
        </Flex>
        <FlexBox
          padding='large'
          backgroundColor='badge.normal'
          flexDirection={['column', 'row']}
          style={{ margin: '2rem 0' }}>
          <Flex
            flexDirection='column'
            marginBottom={['large', 'large', '', '']}
            marginRight='xLarge'>
            <HeadingBox color='header.secondaryLinkActive'>
              {t('cart.order.contactInformation')}
            </HeadingBox>
            <TextBox color='header.secondaryLinkActive'>
              {viewerData?.contactEmail}
            </TextBox>
            <TextBox color='header.secondaryLinkActive'>
              {maskPhoneNumber(
                viewerData?.phoneNumbers?.[0]?.number ||
                  viewerData?.partnerUsers?.edges[0]?.node?.partnerAccount
                    .primaryPhoneNumber,
                'Order'
              )}
            </TextBox>
          </Flex>
          {hasProduct && (
            <Flex
              flexDirection='column'
              marginBottom={['large', 'large', '', '']}>
              <HeadingBox color='header.secondaryLinkActive'>
                {t('cart.order.shippingAddress')}
              </HeadingBox>
              <TextBox color='header.secondaryLinkActive'>
                {order?.shipTo?.organization}
              </TextBox>
              <TextBox color='header.secondaryLinkActive'>
                {order?.shipTo?.address?.contactName}
              </TextBox>
              <TextBox color='header.secondaryLinkActive'>
                {order?.shipTo?.address?.addressLine1}
              </TextBox>
              <TextBox color='header.secondaryLinkActive'>
                {order?.shipTo?.address?.stateProvinceRegionCode}
              </TextBox>
              <TextBox color='header.secondaryLinkActive'>
                {order?.shipTo?.address?.zipPostalCode}
              </TextBox>
              <TextBox color='header.secondaryLinkActive'>
                {order?.shipTo?.address?.countryCode}
              </TextBox>
            </Flex>
          )}
          <Flex
            flexDirection='column'
            marginBottom={['large', 'large', '', '']}>
            <HeadingBox color='header.secondaryLinkActive'>
              {t('cart.order.billingAddress')}
            </HeadingBox>
            <TextBox color='header.secondaryLinkActive'>
              {order?.billTo?.organization}
            </TextBox>
            <TextBox color='header.secondaryLinkActive'>
              {order?.billTo?.firstName} {order?.billTo?.lastName}
            </TextBox>
            <TextBox color='header.secondaryLinkActive'>
              {order?.billTo?.address?.addressLine1}
            </TextBox>
            <TextBox color='header.secondaryLinkActive'>
              {order?.billTo?.address?.city}
              {', '}
              {order?.billTo?.address?.stateProvinceRegionCode}{' '}
              {order?.billTo?.address?.zipPostalCode}
            </TextBox>
            <TextBox color='header.secondaryLinkActive'>
              {t(getFullCountryByCode(order?.billTo?.address?.countryCode))}
            </TextBox>
          </Flex>
          <Flex
            marginBottom={['large', 'large', '', '']}
            marginLeft={['', 'xLarge']}>
            <Flex flexDirection='column'>
              <HeadingBox color='header.secondaryLinkActive'>
                {t('cart.payment.paymentMethod')}
              </HeadingBox>
              {order?.paymentSource?.type === 'PAYPAL' ? (
                <Flex>
                  <PaypalIcon />
                </Flex>
              ) : (
                <>
                  <Flex>
                    <TextBox color='header.secondaryLinkActive'>
                      {order?.paymentSource?.creditCard?.brand}
                    </TextBox>
                    <CreditCardTypeImage
                      className={
                        order?.paymentSource?.creditCard?.brand ===
                        'AMERICAN_EXPRESS'
                          ? 'mediaQueryForAmex'
                          : ''
                      }
                      src={getSmallerCCimg(
                        order?.paymentSource?.creditCard?.brand
                      )}
                      alt='payment-card'
                      marginLeft='0.625rem'
                      width='1.875rem'
                    />
                  </Flex>
                  <TextBox color='header.secondaryLinkActive'>
                    **** **** ****{' '}
                    {order?.paymentSource?.creditCard?.lastFourDigits}
                  </TextBox>
                </>
              )}
            </Flex>
          </Flex>
          <Flex
            marginBottom={['large', 'large', '', '']}
            marginLeft={['', 'xLarge']}>
            <Flex flexDirection='column'>
              <HeadingBox color='text'>
                {t('cart.order.taxExemptionInformation')}
              </HeadingBox>
              <TextBox color='text'>{taxData?.companyName}</TextBox>
              <TextBox color='text'>{taxData?.taxAuthority}</TextBox>
              <TextBox color='text'>
                {t('cart.billing.exemptionStartDateLable')}{' '}
                {localStorage.getItem('temp-tax-start-date')
                  ? localStorage.getItem('temp-tax-start-date')
                  : 'N/A'}
              </TextBox>
              <TextBox color='text'>
                {t('cart.billing.exemptionEndDateLable')}{' '}
                {localStorage.getItem('temp-tax-end-date')
                  ? localStorage.getItem('temp-tax-end-date')
                  : 'N/A'}
              </TextBox>
              <TextBox color='text'>
                {t('cart.billing.certFileName')}{' '}
                {localStorage.getItem('temp-file-name')
                  ? localStorage.getItem('temp-file-name')
                  : 'N/A'}
              </TextBox>
            </Flex>
          </Flex>
        </FlexBox>
        <Flex display='flex' flexDirection='column' style={{ marginTop: 40 }}>
          {order?.lineItems?.map((item, i) => (
            <Flex key={i} display={'flex'} flexDirection={'column'}>
              <CartItem fromThankYou key={i} item={item} />
              <SeparatorLine />
            </Flex>
          ))}
        </Flex>
        <Box>
          {hasProduct && (
            <Box
              display='flex'
              width='100%'
              justifyContent='flex-end'
              paddingBottom='small'>
              <Text width='calc(100% - 7.625rem)' textAlign='right'>
                {t('cart.shipping.shipping')}
              </Text>
              <Text width='7.625rem' textAlign='right'>
                {convertMoneyAmount(
                  {
                    amount: order?.totalShipping?.amount,
                    currencyCode: order?.totalShipping?.currencyCode,
                  },
                  'en-us'
                )}
              </Text>
            </Box>
          )}
          <Box
            display='flex'
            width='100%'
            justifyContent='flex-end'
            paddingBottom='small'>
            <Text width='calc(100% - 7.625rem)' textAlign='right'>
              {t('tax')} {t('notYetCalculated')}
            </Text>

            <Text width='7.625rem' textAlign='right'>
              {convertMoneyAmount(
                {
                  amount: order?.tax?.amount,
                  currencyCode: order?.tax?.currencyCode,
                },
                'en-us'
              )}
            </Text>
          </Box>
        </Box>
        <Box>
          <Box
            display='flex'
            alignItems='center'
            width='100%'
            justifyContent='flex-end'>
            <Heading
              letterSpacing='normal'
              fontSize='medium'
              width='calc(100% - 7.625rem)'
              textAlign='right'>
              {t('totalToday')}
            </Heading>
            <Heading
              letterSpacing='normal'
              width='7.625rem'
              fontSize='medium'
              textAlign='right'>
              {convertMoneyAmount(
                {
                  amount: order?.total?.amount,
                  currencyCode: order?.total?.currencyCode,
                },
                'en-us'
              )}
            </Heading>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default ThankYou;
